// ⌘K palette — fuzzy search across commands, resume text and case studies.
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { buildIndex, search, highlight } from '../../lib/searchIndex';
import { SearchIcon, DownloadIcon } from './Icons';

const GROUP_LABEL = {
  command: 'COMMANDS',
  'case-study': 'CASE STUDIES',
  resume: 'IN RESUME',
  action: 'ACTIONS'
};
const GROUP_ORDER = ['command', 'case-study', 'resume', 'action'];

const Highlighted = ({ text, ranges }) => (
  <>
    {highlight(text, ranges).map(([chunk, isMatch], i) =>
      isMatch ? <mark key={i} className="palette-mark">{chunk}</mark> : <span key={i}>{chunk}</span>
    )}
  </>
);

const CommandPalette = ({ open, onClose, onRun }) => {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const pointerActive = useRef(false);

  const index = useMemo(() => buildIndex(), []);
  const results = useMemo(() => search(index, query), [index, query]);

  // Group while preserving the flat order the arrow keys walk.
  const grouped = useMemo(() => {
    const buckets = new Map();
    results.forEach((r, flatIndex) => {
      const kind = r.entry.kind;
      if (!buckets.has(kind)) buckets.set(kind, []);
      buckets.get(kind).push({ ...r, flatIndex });
    });
    return GROUP_ORDER.filter((k) => buckets.has(k)).map((k) => [k, buckets.get(k)]);
  }, [results]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      // Focus after the element is actually in the DOM.
      const id = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [open]);

  useEffect(() => setActive(0), [query]);

  useEffect(() => {
    const el = listRef.current?.querySelector('[data-active="true"]');
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [active, grouped]);

  // Rows are pointer targets, but scrollIntoView moves them under a
  // resting cursor — which would otherwise walk the selection on its
  // own. Only a real pointer move re-takes control.
  useEffect(() => {
    if (!open) return undefined;
    const onMove = () => { pointerActive.current = true; };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [open]);

  if (!open) return null;

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      pointerActive.current = false;
      setActive((n) => (results.length ? (n + 1) % results.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      pointerActive.current = false;
      setActive((n) => (results.length ? (n - 1 + results.length) % results.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const chosen = results[active];
      if (chosen) {
        onRun(chosen.entry.run);
        onClose();
      }
    } else if (e.key === 'Tab') {
      // Tab completes rather than moving focus — that also keeps focus
      // inside the dialog, so Escape and the arrows keep working.
      e.preventDefault();
      const chosen = results[active];
      if (chosen) setQuery(chosen.entry.run);
    }
  };

  // Close only when the press *starts* on the scrim, so a text-selection
  // drag that ends out here doesn't dismiss the palette.
  const onScrimMouseDown = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="palette-scrim" onMouseDown={onScrimMouseDown}>
      <div
        className="palette"
        role="dialog"
        aria-modal="true"
        aria-label="Search commands and resume"
        onKeyDown={handleKeyDown}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="palette-search">
          <SearchIcon />
          <input
            ref={inputRef}
            className="palette-input"
            value={query}
            placeholder="Search commands and resume…"
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
            spellCheck="false"
          />
          <span className="palette-esc">esc to close</span>
        </div>

        <div className="palette-list" ref={listRef}>
          {results.length === 0 && (
            <div className="palette-empty">No matches for “{query}”</div>
          )}
          {grouped.map(([kind, rows]) => (
            <div key={kind}>
              <div className="palette-group">{GROUP_LABEL[kind]}</div>
              {rows.map((row) => {
                const isActive = row.flatIndex === active;
                const { entry } = row;
                return (
                  <button
                    key={entry.id}
                    type="button"
                    tabIndex={-1}
                    data-active={isActive}
                    className={`palette-row${isActive ? ' palette-row-active' : ''}`}
                    onMouseMove={() => {
                      if (pointerActive.current) setActive(row.flatIndex);
                    }}
                    onClick={() => { onRun(entry.run); onClose(); }}
                  >
                    {entry.kind === 'action' ? (
                      <span className="palette-icon"><DownloadIcon size={14} /></span>
                    ) : (
                      <span className="palette-caret">❯</span>
                    )}
                    <span className="palette-title">
                      {row.field === 'title'
                        ? <Highlighted text={entry.title} ranges={row.ranges} />
                        : entry.title}
                    </span>
                    <span className="palette-sub">
                      {row.field === 'subtitle'
                        ? <Highlighted text={entry.subtitle} ranges={row.ranges} />
                        : entry.subtitle}
                    </span>
                    {isActive && <span className="palette-enter">↵</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="palette-foot">
          <span><b>↑↓</b> navigate</span>
          <span><b>↵</b> run</span>
          <span><b>tab</b> complete</span>
          <span className="palette-count">
            {results.length} result{results.length === 1 ? '' : 's'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
